import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AlertCircle, Lock, Mail, Clock } from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api } from '../lib/api'
import Input from '../components/Input'
import LoadingButton from '../components/LoadingButton'

export default function LoginPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()

  // Form State (Preserved on failure)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Validation & Error State
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [serverError, setServerError] = useState('')

  // Lockout State & Countdown Timer
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0)

  // Loading Button State
  const [buttonState, setButtonState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  // Check saved lockout on mount
  useEffect(() => {
    const savedLockout = localStorage.getItem('admin_lockout_until')
    if (savedLockout) {
      const until = parseInt(savedLockout, 10)
      if (until > Date.now()) {
        setLockoutUntil(until)
      } else {
        localStorage.removeItem('admin_lockout_until')
      }
    }
  }, [])

  // Live countdown timer effect
  useEffect(() => {
    if (!lockoutUntil) {
      setRemainingSeconds(0)
      return
    }

    const tick = () => {
      const now = Date.now()
      const diff = Math.ceil((lockoutUntil - now) / 1000)

      if (diff <= 0) {
        setLockoutUntil(null)
        setRemainingSeconds(0)
        localStorage.removeItem('admin_lockout_until')
        setServerError('')
      } else {
        setRemainingSeconds(diff)
      }
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [lockoutUntil])

  const extractLockoutMinutes = (msg: string): number => {
    const match = msg.match(/try again in (\d+)\s*minute/i)
    if (match && match[1]) {
      return parseInt(match[1], 10)
    }
    return 5 // Default lockout duration: 5 mins
  }

  const formatCountdown = (totalSecs: number): string => {
    const mins = Math.floor(totalSecs / 60)
    const secs = totalSecs % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const validateEmail = (val: string): boolean => {
    if (!val.trim()) {
      setEmailError('Email address is required')
      return false
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(val.trim())) {
      setEmailError('Please enter a valid email address')
      return false
    }
    setEmailError('')
    return true
  }

  const validatePassword = (val: string): boolean => {
    if (!val) {
      setPasswordError('Password is required')
      return false
    }
    if (val.length < 6) {
      setPasswordError('Password must be at least 6 characters')
      return false
    }
    setPasswordError('')
    return true
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // 1. Prevent default browser submission & page refresh
    e.preventDefault()
    e.stopPropagation()

    // Block submission if account is currently locked out
    if (lockoutUntil && remainingSeconds > 0) {
      toast.error(`Account is locked. Please try again in ${formatCountdown(remainingSeconds)}.`)
      return
    }

    setServerError('')
    setEmailError('')
    setPasswordError('')

    // 2. Perform inline validations
    const isEmailValid = validateEmail(email)
    const isPasswordValid = validatePassword(password)

    if (!isEmailValid || !isPasswordValid) {
      toast.error('Please check your login details')
      return
    }

    // 3. Set loading state & disable form
    setButtonState('loading')

    try {
      const response = await api.login(email.trim(), password)
      const data = response.data?.data

      if (!data || data.user?.role !== 'ADMIN') {
        const adminErr = 'Only admin accounts can access this portal'
        setServerError(`❌ ${adminErr}`)
        toast.error(adminErr)
        setButtonState('error')
        setTimeout(() => setButtonState('idle'), 2000)
        return
      }

      // Successful Auth - Clear any lockout
      localStorage.removeItem('admin_lockout_until')
      setLockoutUntil(null)

      setAuth(data.accessToken, data.refreshToken, data.user.id, data.user.role)
      setButtonState('success')
      toast.success('Login successful! Redirecting...')

      setTimeout(() => {
        navigate('/')
      }, 500)
    } catch (err: any) {
      console.error('[LoginPage] Auth error:', err)

      // Reset button loading state safely without page reload
      setButtonState('error')
      setTimeout(() => setButtonState('idle'), 2000)

      const status = err.response?.status
      const apiMessage = err.response?.data?.message || err.message || ''

      const isLocked = status === 429 || /account (is )?locked/i.test(apiMessage) || /too many failed/i.test(apiMessage)

      if (isLocked) {
        const mins = extractLockoutMinutes(apiMessage)
        const until = Date.now() + mins * 60 * 1000
        setLockoutUntil(until)
        localStorage.setItem('admin_lockout_until', until.toString())
        setServerError(`🔒 ${apiMessage}`)
        toast.error(apiMessage)
      } else {
        let displayMessage = apiMessage ? `❌ ${apiMessage}` : '❌ Something went wrong. Please try again.'
        if (!err.response) {
          displayMessage = '❌ Network error. Please check your internet connection.'
        }
        setServerError(displayMessage)
        toast.error(apiMessage || 'Sign in failed')
      }
    }
  }

  const isFormDisabled = buttonState === 'loading' || Boolean(lockoutUntil && remainingSeconds > 0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-[#F6EEE7] to-gray-100 flex items-center justify-center px-4 py-8">
      {/* Background accent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B4513] rounded-full mix-blend-multiply filter blur-3xl opacity-10" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#6D3A0D] rounded-full mix-blend-multiply filter blur-3xl opacity-10" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-[#F6EEE7]">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#6D3A0D] to-[#8B4513] px-8 py-10">
            <div className="flex items-center justify-center mb-3">
              <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                <Lock className="text-white" size={28} />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white text-center">Te Amo Admin</h1>
            <p className="text-amber-100 text-center text-sm mt-1">Sign in to your administration dashboard</p>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            {/* Lockout Live Countdown Banner */}
            {lockoutUntil && remainingSeconds > 0 ? (
              <div className="mb-6 rounded-2xl bg-gradient-to-r from-red-50 to-amber-50 border-2 border-red-200 p-5 shadow-sm text-red-900 animate-fade-in">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2.5 bg-red-100 rounded-xl text-red-700 flex-shrink-0">
                    <Lock size={22} className="animate-pulse" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base text-red-900">Account Temporarily Locked</h3>
                    <p className="text-xs text-red-700">Too many failed login attempts.</p>
                  </div>
                </div>

                <div className="mt-4 bg-white/90 rounded-xl p-3 flex items-center justify-between border border-red-200 shadow-inner">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-red-800">
                    <Clock size={12} className="text-red-600 animate-spin" style={{ animationDuration: '3s' }} />
                    <span>Time Remaining:</span>
                  </div>
                  <span className="font-mono text-lg font-black text-red-700 bg-red-50 px-3.5 py-1 rounded-lg border border-red-200 tracking-wider">
                    {formatCountdown(remainingSeconds)}
                  </span>
                </div>

                <p className="mt-3 text-xs text-red-600 text-center font-medium">
                  Sign in is disabled. Please wait for the countdown to expire.
                </p>
              </div>
            ) : serverError ? (
              <div className="mb-6 flex items-start gap-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm animate-fade-in">
                <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="font-medium">{serverError}</div>
              </div>
            ) : null}

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <Input
                type="email"
                label="Email Address"
                icon={<Mail size={18} />}
                value={email}
                error={emailError}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (emailError) validateEmail(e.target.value)
                }}
                placeholder="admin@example.com"
                autoComplete="email"
                disabled={isFormDisabled}
                required
              />

              <Input
                type="password"
                label="Password"
                icon={<Lock size={18} />}
                value={password}
                error={passwordError}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (passwordError) validatePassword(e.target.value)
                }}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isFormDisabled}
                required
              />

              <div className="flex justify-end pt-1">
                <Link
                  to="/forgot-password"
                  className="text-sm font-semibold text-[#6D3A0D] hover:text-[#5D2A0D] hover:underline transition-colors"
                >
                  Forgot Password?
                </Link>
              </div>

              <LoadingButton
                type="submit"
                state={buttonState}
                disabled={isFormDisabled}
                variant="primary"
                size="lg"
                className={`w-full mt-6 text-white font-semibold py-3 transition-all ${lockoutUntil && remainingSeconds > 0
                  ? 'bg-gray-400 cursor-not-allowed opacity-75'
                  : 'bg-[#6D3A0D] hover:bg-[#5D2A0D]'
                  }`}
                loadingText="Signing In..."
                successText="Logged In!"
                errorText="Sign In Failed"
              >
                {lockoutUntil && remainingSeconds > 0 ? (
                  <span className="flex items-center justify-center gap-2">
                    <Lock size={18} />
                    Locked ({formatCountdown(remainingSeconds)})
                  </span>
                ) : (
                  'Sign In'
                )}
              </LoadingButton>
            </form>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 bg-[#F6EEE7]/60 border-t-2 border-[#F6EEE7] text-center">
            <p className="text-xs text-[#6D3A0D] font-medium">
              © 2026 Te Amo E-Commerce Admin Panel
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
